import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import { Action, BlueprintVersionedGroup } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

const DELETE_TEMPLATE_ACCESS_TYPES = [Action.SpaceAdmin, Action.DeleteBlueprintsVersionedGroup];

/**
 * Hook that returns a function to check if the current user can delete a template.
 * A user can delete a template if:
 * - The template contains the `canDelete` property (true)
 * - AND (they're admin OR they have write access to the space with the DeleteBlueprintsVersionedGroup permission)
 * @returns {(template: BlueprintVersionedGroup) => AccessResult} Function that takes a template and returns an AccessResult indicating if user can delete it
 */
const useCanDeleteTemplate = (): ((template: BlueprintVersionedGroup) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (template: BlueprintVersionedGroup) => {
      const hasDeletePermission = hasAtLeastOneRequiredAction(
        DELETE_TEMPLATE_ACCESS_TYPES,
        viewer,
        template.space.id
      );

      if (!hasDeletePermission) {
        return { result: false, message: NO_ACCESS_MESSAGE };
      }

      if (!template.canDelete) {
        return {
          result: false,
          message: "Template cannot be deleted because it has versions",
        };
      }

      return { result: true };
    },
    [viewer]
  );
};

export default useCanDeleteTemplate;
